import React, { useState, useEffect } from 'react';
import axios from 'axios';

import { motion, AnimatePresence } from 'framer-motion';
import LoadingPage from '../components/Loader';
import UserFeeApproval from '../components/UserFeeApproval';
import SaveOrderAsQuickReorder from '../components/SaveOrderAsQuickReorder';
import StarRating from '../components/rating/StarRating';
import RatingForm from '../components/rating/RatingForm';
import { 
  FaHistory, 
  FaReceipt, 
  FaCalendarAlt, 
  FaClock, 
  FaMoneyBillWave,
  FaChevronDown,
  FaChevronUp,
  FaCheck,
  FaTimes,
  FaShoppingBag,
  FaUtensils,
  FaSyncAlt,
  FaFilter,
  FaSearch,
  FaTags,
  FaStar,
  FaEdit
} from 'react-icons/fa';
import { getUserRating } from '../utils/restaurantApi';
import 'react-toastify/dist/ReactToastify.css';
import { showSuccessNotification, showErrorNotification, showInfoNotification, showWarningNotification } from '../components/NotificationManager';

// Add custom animation class for slow spin
import './order-history.css';

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [processingOrders, setProcessingOrders] = useState({});
  const [page, setPage] = useState(1);
  const [hasMoreOrders, setHasMoreOrders] = useState(true);
  const [totalOrders, setTotalOrders] = useState(0);
  const [currentlyShowing, setCurrentlyShowing] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showFeeApprovalModal, setShowFeeApprovalModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  
  // Rating-related states
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [selectedOrderForRating, setSelectedOrderForRating] = useState(null);
  const [userRatings, setUserRatings] = useState({});
  const [loadingRatings, setLoadingRatings] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const ordersPerPage = 100;

  const fetchOrderHistory = async (isLoadMore = false) => {
    const token = localStorage.getItem('token');

    if (token) {
      try {
        const currentPage = isLoadMore ? page : 1;
        const response = await axios.get(
          `https://mongobyte-hc73.onrender.com/api/v1/users/my-orders?page=${currentPage}&limit=${ordersPerPage}`,
          { 
            headers: { Authorization: `Bearer ${token}` },
            timeout: 30000 // 30 seconds timeout for order history requests
          }
        );

        // Handle the API response structure: { success: true, data: { orders: [...], pagination: {...} } }
        let newOrders = [];
        let paginationInfo = { currentPage: 1, totalPages: 1, totalItems: 0 };

        if (response.data && response.data.success && response.data.data) {
          // Expected structure: { success: true, data: { orders: [...], pagination: {...} } }
          const responseData = response.data.data;
          newOrders = responseData.orders || [];
          paginationInfo = responseData.pagination || paginationInfo;
        } 
        // Fallback for direct structure: { orders: [...], pagination: {...} }
        else if (response.data && response.data.orders) {
          newOrders = response.data.orders || [];
          paginationInfo = response.data.pagination || paginationInfo;
        }
        // Fallback: if response.data is directly an array of orders
        else if (Array.isArray(response.data)) {
          newOrders = response.data;
          paginationInfo = { 
            currentPage: 1, 
            totalPages: 1, 
            totalItems: response.data.length 
          };
        }
        
        if (isLoadMore) {
          setOrders((prevOrders) => [...prevOrders, ...newOrders]);
        } else {
          setOrders(newOrders);
        }
        
        // Update total count and currently showing count
        setTotalOrders(paginationInfo.totalItems || 0);
        setCurrentlyShowing(isLoadMore ? orders.length + newOrders.length : newOrders.length);
        
        // Apply filters to the new orders
        const allOrders = isLoadMore ? [...orders, ...newOrders] : newOrders;
        applyFilters(allOrders);

        // Fetch ratings for delivered orders
        await fetchUserRatingsForOrders(allOrders);

        // Safe pagination handling
        const hasMore = paginationInfo && 
                       paginationInfo.currentPage && 
                       paginationInfo.totalPages && 
                       paginationInfo.currentPage < paginationInfo.totalPages;
        setHasMoreOrders(hasMore);
      } catch (error) {
        handleAxiosError(error, 'Failed to load order history.');
      } finally {
        setLoading(false);
      }
    } else {
      showErrorNotification('No user token found. Please log in.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderHistory();
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply filters whenever filters change
  useEffect(() => {
    applyFilters();
  }, [filters, orders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter functions
  const applyFilters = () => {
    let filtered = [...orders];

    // Status filter
    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter(order => 
        order.status === filters.status
      );
    }

    // Search filter (order ID, restaurant name, meal names)
    if (filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      filtered = filtered.filter(order => {
        const orderIdMatch = order.customId?.toLowerCase().includes(searchTerm);
        const restaurantMatch = order.restaurant?.name?.toLowerCase().includes(searchTerm);
        const mealMatch = order.meals?.some(meal => 
          meal.meal?.name?.toLowerCase().includes(searchTerm)
        );
        return orderIdMatch || restaurantMatch || mealMatch;
      });
    }

    // Date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(order => 
        new Date(order.createdAt) >= fromDate
      );
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(order => 
        new Date(order.createdAt) <= toDate
      );
    }

    // Amount range filter
    if (filters.minAmount) {
      filtered = filtered.filter(order => 
        order.totalPrice >= parseFloat(filters.minAmount)
      );
    }

    if (filters.maxAmount) {
      filtered = filtered.filter(order => 
        order.totalPrice <= parseFloat(filters.maxAmount)
      );
    }

    setFilteredOrders(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      search: '',
      dateFrom: '',
      dateTo: '',
      minAmount: '',
      maxAmount: ''
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.status && filters.status !== 'all') count++;
    if (filters.search.trim()) count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    if (filters.minAmount) count++;
    if (filters.maxAmount) count++;
    return count;
  };

  const handleExpandClick = (orderId) => {
    setExpandedOrders((prevState) => ({
      ...prevState,
      [orderId]: !prevState[orderId],
    }));
  };

  const handleAcceptFee = (order) => {
    setSelectedOrder(order);
    setShowFeeApprovalModal(true);
  };

  const handleOrderUpdate = (updatedOrder) => {
    setOrders(prevOrders => 
      prevOrders.map(order => 
        order.customId === updatedOrder.customId ? updatedOrder : order
      )
    );
    setSelectedOrder(null);
    setShowFeeApprovalModal(false);
  };

  const closeFeeApprovalModal = () => {
    setSelectedOrder(null);
    setShowFeeApprovalModal(false);
  };

  // Rating-related functions
  const fetchUserRatingsForOrders = async (ordersData) => {
    if (!ordersData || ordersData.length === 0) return;
    
    setLoadingRatings(true);
    const deliveredOrders = ordersData.filter(order => order.status === 'Delivered');
    
    const ratingsPromises = deliveredOrders.map(async (order) => {
      try {
        // Extract restaurant ID from order
        const restaurantId = order.restaurant?.customId || order.restaurantId;
        if (!restaurantId) return null;
        
        const rating = await getUserRating(restaurantId);
        return { restaurantId, rating };
      } catch (error) {
        // User hasn't rated this restaurant yet
        return { restaurantId: order.restaurant?.customId || order.restaurantId, rating: null };
      }
    });

    try {
      const ratingsResults = await Promise.all(ratingsPromises);
      const ratingsMap = {};
      ratingsResults.forEach((result) => {
        if (result && result.restaurantId) {
          ratingsMap[result.restaurantId] = result.rating;
        }
      });
      setUserRatings(ratingsMap);
    } catch (error) {
      console.error('Error fetching user ratings:', error);
    } finally {
      setLoadingRatings(false);
    }
  };

  const handleRateRestaurant = (order) => {
    setSelectedOrderForRating(order);
    setShowRatingForm(true);
  };

  const handleRatingSuccess = (result) => {
    setShowRatingForm(false);
    setSelectedOrderForRating(null);
    
    // Update the ratings cache
    if (selectedOrderForRating) {
      const restaurantId = selectedOrderForRating.restaurant?.customId || selectedOrderForRating.restaurantId;
      setUserRatings(prev => ({
        ...prev,
        [restaurantId]: result.rating || result
      }));
    }
    
    showSuccessNotification('Rating submitted successfully!');
  };

  const canRateOrder = (order) => {
    return order.status === 'Delivered' && (order.restaurant?.customId || order.restaurantId);
  };

  const getUserRatingForOrder = (order) => {
    const restaurantId = order.restaurant?.customId || order.restaurantId;
    return userRatings[restaurantId] || null;
  };

  const handleCancelOrder = async (orderId) => {
    setProcessingOrders((prevState) => ({
      ...prevState,
      [orderId]: true,
    }));
    showInfoNotification('Please wait while the order is being canceled...');

    try {
      await axios.post(
        `https://mongobyte-hc73.onrender.com/api/v1/orders/${orderId}/status`,
        { action: 'cancel' },
        { 
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          timeout: 15000 // 15 seconds timeout for cancel operations
        }
      );
      showSuccessNotification('Order canceled successfully');
      fetchOrderHistory();
    } catch (error) {
      handleAxiosError(error, 'Failed to cancel order.');
    } finally {
      setProcessingOrders((prevState) => ({
        ...prevState,
        [orderId]: false,
      }));
    }
  };

  const handleAxiosError = (error, defaultMessage) => {
    if (error.response) {
      console.error('Response error:', error.response);
      showErrorNotification(error.response.data.message || defaultMessage);
    } else if (error.request) {
      console.error('Request error:', error.request);
      showErrorNotification('No response received from the server.');
    } else {
      console.error('Error:', error.message);
      showErrorNotification(defaultMessage);
    }
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    setPage((prevPage) => prevPage + 1);
    await fetchOrderHistory(true);
    setLoadingMore(false);
  };

  const handleLoadAllOrders = async () => {
    setLoadingAll(true);
    const token = localStorage.getItem('token');
    
    if (token) {
      try {
        let allOrders = [];
        let currentPage = 1;
        let totalPages = 1;
        
        // Keep loading pages until we get all orders
        do {
          const response = await axios.get(
            `https://mongobyte-hc73.onrender.com/api/v1/users/my-orders?page=${currentPage}&limit=100`, // Use reasonable page size
            { 
              headers: { Authorization: `Bearer ${token}` },
              timeout: 45000
            }
          );

          let pageOrders = [];
          let paginationInfo = { currentPage: 1, totalPages: 1, totalItems: 0 };

          if (response.data && response.data.success && response.data.data) {
            const responseData = response.data.data;
            pageOrders = responseData.orders || [];
            paginationInfo = responseData.pagination || paginationInfo;
          } 
          else if (response.data && response.data.orders) {
            pageOrders = response.data.orders || [];
            paginationInfo = response.data.pagination || paginationInfo;
          }
          else if (Array.isArray(response.data)) {
            pageOrders = response.data;
            paginationInfo = { 
              currentPage: currentPage, 
              totalPages: currentPage, 
              totalItems: response.data.length 
            };
          }
          
          allOrders = [...allOrders, ...pageOrders];
          totalPages = paginationInfo.totalPages || 1;
          currentPage++;
          
        } while (currentPage <= totalPages);
        
        setOrders(allOrders);
        setTotalOrders(allOrders.length);
        setCurrentlyShowing(allOrders.length);
        setHasMoreOrders(false); // We loaded all orders, so no more to load
        setPage(1); // Reset page
        
        // Apply filters to all orders
        applyFilters(allOrders);
        
        showSuccessNotification(`Loaded all ${allOrders.length} orders!`);
      } catch (error) {
        handleAxiosError(error, 'Failed to load all orders.');
      }
    }
    setLoadingAll(false);
  };

  if (loading) {
    return <LoadingPage />;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'text-yellow-600 bg-yellow-100';
      case 'Confirmed': return 'text-primary-600 bg-primary-100';
      case 'Delivered': return 'text-green-600 bg-green-100';
      case 'Canceled': return 'text-red-600 bg-red-100';
      case 'Fee Requested': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-6 px-4 pt-16 md:pt-24 pb-24 md:pb-6">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-crust mb-2 flex items-center justify-center gap-3 font-secondary">
            <FaHistory className="text-pepperoni" />
            Order History
          </h1>
          <p className="text-gray-600 font-sans">
            Track all your delicious orders
            {loadingRatings && (
              <span className="ml-2 text-sm text-orange-600">
                • Loading ratings...
              </span>
            )}
          </p>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setIsRefreshing(true);
              const refreshToast = showInfoNotification("Refreshing orders...");
              fetchOrderHistory(false)
                .then(() => {
                  showSuccessNotification("Orders refreshed successfully!");
                })
                .catch((error) => {
                  showErrorNotification("Failed to refresh orders. Please try again.");
                })
                .finally(() => {
                  setIsRefreshing(false);
                });
            }}
            disabled={isRefreshing}
            className="mt-4 flex items-center gap-2 bg-pepperoni text-white px-4 py-2 rounded-full mx-auto shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-70"
          >
            <FaSyncAlt className={isRefreshing ? "refresh-spin" : ""} />
            <span>{isRefreshing ? "Refreshing..." : "Refresh Orders"}</span>
          </motion.button>
        </motion.div>

        {/* Filters Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FaFilter className="text-pepperoni" />
              <h2 className="text-lg font-semibold text-crust">Filter Orders</h2>
              {getActiveFilterCount() > 0 && (
                <span className="bg-pepperoni text-white text-xs px-2 py-1 rounded-full">
                  {getActiveFilterCount()} active
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {getActiveFilterCount() > 0 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={clearFilters}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Clear All
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-gray-600 hover:text-crust"
              >
                <span className="text-sm font-medium">
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </span>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              </motion.button>
            </div>
          </div>

          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-4"
              >
                {/* Quick Filter Presets */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Quick Filters</label>
                  <div className="flex flex-wrap gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleFilterChange('status', 'delivered')}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        filters.status === 'Delivered' 
                          ? 'bg-green-500 text-white' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      Delivered Orders
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleFilterChange('status', 'in progress')}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        filters.status === 'in progress' 
                          ? 'bg-primary-500 text-white' 
                          : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                      }`}
                    >
                      In Progress
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleFilterChange('status', 'fee requested')}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        filters.status === 'fee requested' 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                      }`}
                    >
                      Fee Requested
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        const lastWeek = new Date();
                        lastWeek.setDate(lastWeek.getDate() - 7);
                        handleFilterChange('dateFrom', lastWeek.toISOString().split('T')[0]);
                      }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        filters.dateFrom === new Date(Date.now() - 7*24*60*60*1000).toISOString().split('T')[0]
                          ? 'bg-purple-500 text-white' 
                          : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      }`}
                    >
                      Last 7 Days
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        const lastMonth = new Date();
                        lastMonth.setMonth(lastMonth.getMonth() - 1);
                        handleFilterChange('dateFrom', lastMonth.toISOString().split('T')[0]);
                      }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        filters.dateFrom === new Date(Date.now() - 30*24*60*60*1000).toISOString().split('T')[0]
                          ? 'bg-indigo-500 text-white' 
                          : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                      }`}
                    >
                      Last 30 Days
                    </motion.button>
                  </div>
                </div>

                {/* Search Bar */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaSearch className="inline mr-2" />
                    Search Orders
                  </label>
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    placeholder="Search by order ID, restaurant, or meal name..."
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pepperoni focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FaTags className="inline mr-2" />
                      Order Status
                    </label>
                    <select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pepperoni focus:border-transparent"
                    >
                      <option value="all">All Statuses</option>
                      <option value="Pending">Pending</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Canceled">Canceled</option>
                      <option value="Fee Requested">Fee Requested</option>
                      <option value="Delivered">Delivered</option>
                    </select>
                  </div>

                  {/* Date From */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FaCalendarAlt className="inline mr-2" />
                      From Date
                    </label>
                    <input
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pepperoni focus:border-transparent"
                    />
                  </div>

                  {/* Date To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FaCalendarAlt className="inline mr-2" />
                      To Date
                    </label>
                    <input
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pepperoni focus:border-transparent"
                    />
                  </div>

                  {/* Min Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FaMoneyBillWave className="inline mr-2" />
                      Min Amount (₦)
                    </label>
                    <input
                      type="number"
                      value={filters.minAmount}
                      onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                      placeholder="0"
                      min="0"
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pepperoni focus:border-transparent"
                    />
                  </div>

                  {/* Max Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FaMoneyBillWave className="inline mr-2" />
                      Max Amount (₦)
                    </label>
                    <input
                      type="number"
                      value={filters.maxAmount}
                      onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                      placeholder="No limit"
                      min="0"
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pepperoni focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Filter Results Summary */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-sm text-gray-600">
                      Showing {filteredOrders.length} of {orders.length} loaded orders
                    </span>
                    {totalOrders > 0 && (
                      <span className="text-sm text-primary-600 font-medium">
                        {currentlyShowing} of {totalOrders} total orders loaded
                      </span>
                    )}
                    {filteredOrders.length !== orders.length && (
                      <span className="text-sm text-pepperoni font-medium">
                        {orders.length - filteredOrders.length} orders filtered out
                      </span>
                    )}
                  </div>
                  {hasMoreOrders && (
                    <div className="mt-2 p-2 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-sm text-yellow-700">
                        <strong>Note:</strong> There are more orders available. 
                        {filteredOrders.length === 0 && filters.status !== 'all' ? 
                          ' Your filtered results might be in the next batch. Try loading more orders or clearing filters.' :
                          ' Load more to see additional orders.'
                        }
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {filteredOrders && filteredOrders.length === 0 && orders.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="mb-6">
              <FaFilter className="text-6xl text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-600 mb-2">No orders match your filters</h2>
              <p className="text-gray-500 mb-4">
                {hasMoreOrders ? 
                  'Your filtered results might be in the next batch of orders. Try loading more orders first.' :
                  'Try adjusting your filter criteria'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={clearFilters}
                  className="bg-pepperoni text-white px-6 py-2 rounded-xl font-semibold"
                >
                  Clear All Filters
                </motion.button>
                {hasMoreOrders && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="bg-primary-600 text-white px-6 py-2 rounded-xl font-semibold disabled:opacity-50"
                  >
                    {loadingMore ? 'Loading...' : 'Load More Orders'}
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        ) : orders && orders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="mb-6">
              <FaShoppingBag className="text-6xl text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-600 mb-2">No orders found</h2>
              <p className="text-gray-500">You haven't placed any orders yet!</p>
            </div>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {filteredOrders && filteredOrders.map((order, index) => (
                <motion.div
                  key={order._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                >
                  {/* Order Header */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-pepperoni/10 p-3 rounded-full">
                          <FaReceipt className="text-pepperoni text-lg" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-crust">Order #{order.customId}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <FaCalendarAlt className="text-gray-400 text-sm" />
                            <span className="text-sm text-gray-600 font-sans">
                              {new Date(order.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right space-y-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                        {/* Show save button for completed/delivered orders */}
                        {(order.status === 'Delivered' || order.status === 'Completed') && (
                          <div className="mt-2">
                            <SaveOrderAsQuickReorder
                              orderId={order._id || order.customId}
                              orderData={order}
                              userToken={localStorage.getItem('token')}
                              onSaveSuccess={(savedOrder) => {
                                showSuccessNotification(`"${savedOrder.name}" saved as Quick Reorder! 💖`);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Order Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                      {order.status === "Fee Requested" ? (
                        <>
                          <div className="bg-gray-50 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <FaUtensils className="text-green-600 text-sm" />
                              <span className="text-sm text-gray-600 font-sans">Meal Total</span>
                            </div>
                            <span className="text-lg font-bold text-crust">₦{order.foodAmount || (order.totalPrice - order.fee)}</span>
                          </div>
                          <div className="bg-primary-50 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <FaClock className="text-primary-600 text-sm" />
                              <span className="text-sm text-gray-600 font-sans">Your Allocated Fee</span>
                            </div>
                            <span className="text-lg font-bold text-crust">₦{order.fee}</span>
                          </div>
                          <div className="bg-orange-50 rounded-xl p-3 col-span-2 md:col-span-1">
                            <div className="flex items-center gap-2 mb-1">
                              <FaMoneyBillWave className="text-orange-600 text-sm" />
                              <span className="text-sm text-gray-600 font-sans">Restaurant Requested</span>
                            </div>
                            <span className="text-lg font-bold text-crust">₦{order.requestedFee || 0}</span>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <FaUtensils className="text-orange-600 text-sm" />
                              <span className="text-sm text-gray-600 font-sans">Items</span>
                            </div>
                            <span className="text-lg font-bold text-crust">{order.meals ? order.meals.length : 0}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="bg-gray-50 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <FaMoneyBillWave className="text-green-600 text-sm" />
                              <span className="text-sm text-gray-600 font-sans">Total</span>
                            </div>
                            <span className="text-lg font-bold text-crust">₦{order.totalPrice}</span>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <FaClock className="text-primary-600 text-sm" />
                              <span className="text-sm text-gray-600 font-sans">Fee</span>
                            </div>
                            <span className="text-lg font-bold text-crust">₦{order.fee}</span>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-3 col-span-2 md:col-span-1">
                            <div className="flex items-center gap-2 mb-1">
                              <FaUtensils className="text-orange-600 text-sm" />
                              <span className="text-sm text-gray-600 font-sans">Items</span>
                            </div>
                            <span className="text-lg font-bold text-crust">{order.meals ? order.meals.length : 0}</span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Fee Request Description */}
                    {order.status === "Fee Requested" && order.requestDescription && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                        <div className="flex items-start gap-2">
                          <FaMoneyBillWave className="text-yellow-600 text-sm mt-0.5" />
                          <div>
                            <h4 className="font-semibold text-yellow-800 mb-1">Why this fee was requested:</h4>
                            <p className="text-sm text-yellow-700">{order.requestDescription}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Rating Section for Delivered Orders */}
                    {canRateOrder(order) && (
                      <div className="border-t border-gray-200 pt-4 mt-4">
                        {getUserRatingForOrder(order) ? (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                            {/* <span className="text-sm text-gray-600">Your rating:</span> */}
                              <StarRating 
                                value={getUserRatingForOrder(order)?.overallRating || getUserRatingForOrder(order)?.ratings?.overall || 0} 
                                readonly 
                                size="sm" 
                                showValue 
                              />
                              {(getUserRatingForOrder(order)?.review || getUserRatingForOrder(order)?.reviewText) && (
                                <span className="text-sm text-gray-500 italic">
                                  "{(getUserRatingForOrder(order)?.review || getUserRatingForOrder(order)?.reviewText).slice(0, 50)}{(getUserRatingForOrder(order)?.review || getUserRatingForOrder(order)?.reviewText).length > 50 ? '...' : ''}"
                                </span>
                              )}
                            </div>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleRateRestaurant(order)}
                              className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium text-sm"
                            >
                              <FaEdit />
                             
                            </motion.button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">
                              How was your experience with {order.restaurant?.name || 'this restaurant'}?
                            </span>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleRateRestaurant(order)}
                              className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 font-medium text-sm"
                            >
                              <FaStar />
                              Rate Restaurant
                            </motion.button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Expand Button */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleExpandClick(order._id)}
                      className="w-full flex items-center justify-center gap-2 py-3 text-gray-600 hover:text-crust transition-colors duration-200 border-t border-gray-200 mt-4"
                    >
                      <span className="font-semibold">
                        {expandedOrders[order._id] ? 'Show Less' : 'Show Details'}
                      </span>
                      {expandedOrders[order._id] ? <FaChevronUp /> : <FaChevronDown />}
                    </motion.button>
                  </div>

                  {/* Expanded Details */}
                  <AnimatePresence>
                    {expandedOrders[order._id] && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-t border-gray-200 bg-gray-50"
                      >
                        <div className="p-6 space-y-4">
                          <h4 className="font-bold text-crust mb-3 flex items-center gap-2">
                            <FaUtensils className="text-orange-600" />
                            Meal Details
                          </h4>
                          <div className="space-y-3">
                            {order.meals && order.meals.map((mealDetail, index) => (
                              <div key={mealDetail.meal._id} className="bg-white rounded-xl p-4 border border-gray-200">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <h5 className="font-semibold text-crust">{mealDetail.meal.name}</h5>
                                    <p className="text-sm text-gray-600 mt-1">Quantity: {mealDetail.quantity}</p>
                                  </div>
                                  <span className="font-bold text-pepperoni">₦{mealDetail.meal.price}</span>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Action Buttons */}
                          {order.status === "Fee Requested" && (
                            <div className="flex gap-3 pt-4">
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => handleAcceptFee(order)}
                                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                              >
                                <FaCheck />
                                Review Fee Request
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => handleCancelOrder(order.customId)}
                                disabled={processingOrders[order.customId]}
                                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                              >
                                <FaTimes />
                                Cancel Order
                              </motion.button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Load More Button */}
            {hasMoreOrders && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center pt-6 space-y-3"
              >
                <div className="text-center mb-2">
                  <p className="text-sm text-gray-600">
                    Showing {currentlyShowing} of {totalOrders} total orders
                  </p>
                  <p className="text-xs text-gray-500">
                    {totalOrders - currentlyShowing} more orders available
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleLoadMore}
                    disabled={loadingMore || loadingAll}
                    className="bg-gradient-to-r from-pepperoni to-red-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingMore ? (
                      <div className="flex items-center gap-2">
                        <FaSyncAlt className="animate-spin" />
                        <span>Loading More...</span>
                      </div>
                    ) : (
                      `Load Next ${Math.min(ordersPerPage, totalOrders - currentlyShowing)}`
                    )}
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleLoadAllOrders}
                    disabled={loadingMore || loadingAll}
                    className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingAll ? (
                      <div className="flex items-center gap-2">
                        <FaSyncAlt className="animate-spin" />
                        <span>Loading All...</span>
                      </div>
                    ) : (
                      `Load All ${totalOrders} Orders`
                    )}
                  </motion.button>
                </div>
              </motion.div>
            )}
            
            {/* End of orders indicator */}
            {!hasMoreOrders && orders.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-center pt-6"
              >
                <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <p className="text-sm text-gray-600 font-medium">
                    🎉 You've reached the end! All {totalOrders} orders loaded.
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Use the filters above to find specific orders
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </div>

      {/* Fee Approval Modal */}
      {showFeeApprovalModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <UserFeeApproval
              order={selectedOrder}
              onOrderUpdate={handleOrderUpdate}
              onClose={closeFeeApprovalModal}
            />
          </div>
        </div>
      )}

      {/* Rating Form Modal */}
      <AnimatePresence>
        {showRatingForm && selectedOrderForRating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowRatingForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <RatingForm
                restaurantId={selectedOrderForRating.restaurant?.customId || selectedOrderForRating.restaurantId}
                restaurantName={selectedOrderForRating.restaurant?.name || 'Restaurant'}
                orderId={selectedOrderForRating.customId || selectedOrderForRating._id}
                existingRating={getUserRatingForOrder(selectedOrderForRating)}
                onCancel={() => setShowRatingForm(false)}
                onSuccess={handleRatingSuccess}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OrderHistory;
